import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { downloadZip } from "client-zip";
import {
  deleteComment,
  getAlbum,
  getDownloadUrls,
  getMe,
  isTrialAlbum,
  listComments,
  listHostPhotos,
  setPhotoHidden,
  showUpgrade,
  type AlbumDetail,
  type HostPhoto,
} from "../../../../lib/hostApi";
import { Comments } from "../../../../components/Comments";
import { ArrowIcon, BackIcon, DownloadIcon, EventPage, EventShell, GearIcon, HERO_BTN, ICON_BTN } from "../../../../components/EventPage";
import { Sheet } from "../../../../components/Sheet";
import { msgText, useT, type Msg } from "../../../../lib/i18n";
import { ApiError } from "../../../../lib/api";
import { uploadClosed } from "../../../../lib/timewindow";

export const Route = createFileRoute("/host/albums/$id/")({ component: AlbumDetailPage });

function AlbumDetailPage() {
  const { t } = useT();
  const { id } = Route.useParams();
  const album = useQuery({ queryKey: ["host-album", id], queryFn: () => getAlbum(id) });
  const me = useQuery({ queryKey: ["me"], queryFn: getMe });
  const [people, setPeople] = useState(false);

  if (album.isPending) return <EventShell>{t("common.loading")}</EventShell>;
  if (album.error || !album.data) return <EventShell>{t("host.album.not_found")}</EventShell>;

  const a = album.data;
  // Top-up tamu ikut ditutup bersama jendela upload.
  const upgrade = !uploadClosed(a, Date.now() / 1000) && <UpgradeCard a={a} />;

  return (
    <EventPage
      album={a}
      joinName={me.data?.name ?? ""}
      hero={
        <>
          <Link to="/host" aria-label={t("common.back")} className={HERO_BTN}>
            <BackIcon />
          </Link>
          <Link to="/host/albums/$id/settings" params={{ id }} aria-label={t("event.settings.title")} className={HERO_BTN}>
            <GearIcon />
          </Link>
        </>
      }
      onPeople={() => setPeople(true)}
      extra={<DownloadZip id={id} slug={a.slug} />}
    >
      {a.status === "pending_payment" ? (
        <Link
          to="/host/albums/$id/pay"
          params={{ id }}
          className="block rounded-2xl border border-amber-400/40 bg-amber-950/30 p-4"
        >
          <p className="font-medium">{t("host.album.pay_now")}</p>
          <p className="text-sm text-neutral-400">{t("host.album.pending_desc")}</p>
        </Link>
      ) : (
        showUpgrade(a) && upgrade
      )}

      <HostPhotos id={id} a={a} />

      {people && (
        <Sheet title={t("host.album.guests_heading", { count: a.devices.length })} onClose={() => setPeople(false)}>
          <div className="space-y-5">
            {a.devices.length === 0 ? (
              <p className="text-sm text-neutral-400">{t("host.album.no_guests")}</p>
            ) : (
              <ul className="-mx-5 divide-y divide-white/10 border-t border-white/10">
                {a.devices.map((d) => (
                  <li key={d.id} className="flex justify-between gap-3 px-5 py-3">
                    <span className="truncate">{d.name ?? t("host.album.guest_untitled")}</span>
                    <span className="shrink-0 text-neutral-400">{t("host.album.guest_photo_count", { count: d.photo_count })}</span>
                  </li>
                ))}
              </ul>
            )}
            {upgrade}
          </div>
        </Sheet>
      )}
    </EventPage>
  );
}

/** Kartu "Tambah pax" → bayar top-up tamu. */
function UpgradeCard({ a }: { a: AlbumDetail }) {
  const { t } = useT();
  return (
    <Link
      to="/host/albums/$id/pay"
      params={{ id: String(a.id) }}
      search={{ kind: "topup_device" }}
      className="flex items-center gap-4 rounded-2xl border border-blue-400/30 bg-blue-950/30 px-5 py-4"
    >
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-400">{t("event.upgrade.label")}</p>
        <p className="font-medium">{t("event.upgrade.title")}</p>
        <p className="text-sm text-neutral-400">
          {isTrialAlbum(a)
            ? t("event.upgrade.trial_sub", { count: a.device_quota })
            : t("event.upgrade.sub", { used: a.device_count, quota: a.device_quota })}
        </p>
      </div>
      <span aria-hidden className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-400 text-ink">
        <ArrowIcon />
      </span>
    </Link>
  );
}

/** Semua foto (termasuk yang disembunyikan), tombol sembunyikan & komentar. */
function HostPhotos({ id, a }: { id: string; a: AlbumDetail }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [openComments, setOpenComments] = useState<number | null>(null);
  const photos = useInfiniteQuery({
    queryKey: ["host-photos", id],
    queryFn: ({ pageParam }) => listHostPhotos(id, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const toggle = useMutation({
    mutationFn: (p: HostPhoto) => setPhotoHidden(id, p.id, p.status !== "hidden"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["host-photos", id] }),
  });
  const items = photos.data?.pages.flatMap((p) => p.items) ?? [];
  const s = a.settings;

  return (
    <section className="space-y-3">
      <div className="grid grid-cols-2 gap-3.5 min-[720px]:grid-cols-3 min-[720px]:gap-[18px]">
        {items.map((p) => (
          <figure key={p.id} className="space-y-1">
            <img
              src={p.thumb_url}
              alt={t("host.album.photo_alt", { name: p.device_name ?? t("guest.guest_fallback") })}
              loading="lazy"
              className={`aspect-square w-full rounded-[22px] object-cover ${p.status === "hidden" ? "opacity-40" : ""}`}
            />
            {((s.likes && p.likes > 0) || (s.comments && p.comments > 0)) && (
              <p className="text-xs text-neutral-400">
                {s.likes && p.likes > 0 && `♥ ${p.likes}`}
                {s.likes && p.likes > 0 && s.comments && p.comments > 0 && " · "}
                {s.comments && p.comments > 0 && `💬 ${p.comments}`}
              </p>
            )}
            <div className="flex gap-3 text-xs text-neutral-300">
              {p.status === "uploading" ? (
                <span className="text-neutral-400">{t("host.album.uploading")}</span>
              ) : (
                <button type="button" className="underline" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
                  {p.status === "hidden" ? t("host.album.show") : t("host.album.hide")}
                </button>
              )}
              {s.comments && (
                <button type="button" className="underline" onClick={() => setOpenComments(p.id)}>
                  {t("host.album.comments_open")}
                </button>
              )}
            </div>
          </figure>
        ))}
      </div>
      {items.length === 0 && !photos.isPending && <p className="text-center text-sm text-neutral-400">{t("host.album.no_photos")}</p>}
      {photos.hasNextPage && (
        <button
          type="button"
          className="w-full rounded-full border border-white/20 px-3 py-2 text-sm"
          onClick={() => photos.fetchNextPage()}
        >
          {t("host.album.load_more")}
        </button>
      )}
      {openComments !== null && (
        <Sheet title={t("host.album.comments_open")} onClose={() => setOpenComments(null)}>
          <HostComments id={id} photoId={openComments} />
        </Sheet>
      )}
    </section>
  );
}

function HostComments({ id, photoId }: { id: string; photoId: number }) {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["host-comments", id, photoId], queryFn: () => listComments(id, photoId) });
  const [delError, setDelError] = useState<Msg | null>(null);
  const del = useMutation({
    mutationFn: (cid: number) => deleteComment(id, cid),
    onSuccess: () => {
      setDelError(null);
      qc.invalidateQueries({ queryKey: ["host-comments", id, photoId] });
      qc.invalidateQueries({ queryKey: ["host-photos", id] });
    },
    onError: (e) => setDelError(e instanceof ApiError ? { text: e.message } : { key: "host.album.comment_delete_failed" }),
  });
  if (q.isPending) return <p className="text-sm text-neutral-400">{t("common.loading")}</p>;
  if (q.error || !q.data) return <p className="text-sm text-red-400">{t("host.album.comments_load_failed")}</p>;
  return (
    <div className="space-y-1">
      <Comments items={q.data.items} canPost={false} onDelete={(cid) => del.mutate(cid)} />
      {delError && (
        <p className="text-sm text-red-400" role="alert">
          {msgText(t, delError)}
        </p>
      )}
    </div>
  );
}

/** Zip dirakit di browser dari presigned URL (desain teknis bagian 8). */
function DownloadZip({ id, slug }: { id: string; slug: string }) {
  const { t } = useT();
  const [state, setState] = useState<"idle" | "working" | "error">("idle");
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);

  async function run() {
    setState("working");
    setDone(0);
    setTotal(0);
    try {
      // URL orig hanya berlaku 5 menit, jadi ambil per halaman tepat sebelum dipakai.
      async function* files() {
        let cursor = 0;
        for (;;) {
          const page = await getDownloadUrls(id, cursor);
          setTotal(page.total);
          for (const it of page.items) {
            let res = await fetch(it.url);
            if (res.status === 403) {
              // Tanda tangan kedaluwarsa di tengah halaman: minta ulang halaman ini sekali.
              const fresh = await getDownloadUrls(id, cursor);
              const again = fresh.items.find((x) => x.id === it.id);
              if (again) res = await fetch(again.url);
            }
            setDone((n) => n + 1);
            yield { name: `${it.id}.jpg`, input: res };
          }
          if (page.next === null) return;
          cursor = page.next;
        }
      }
      const picker = (window as unknown as { showSaveFilePicker?: (o: { suggestedName: string }) => Promise<FileSystemFileHandle> })
        .showSaveFilePicker;
      if (picker) {
        // Streaming ke disk: paket besar (3000 x 3 MB) tidak muat di memori sebagai Blob.
        const handle = await picker.call(window, { suggestedName: `${slug}.zip` });
        await downloadZip(files()).body!.pipeTo(await handle.createWritable());
      } else {
        const url = URL.createObjectURL(await downloadZip(files()).blob());
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}.zip`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
      setState("idle");
    } catch {
      setState("error");
    }
  }

  const label =
    state === "working"
      ? t("host.album.download_preparing", { done, total })
      : state === "error"
        ? t("host.album.download_failed")
        : t("host.album.download_zip");
  return (
    <button
      type="button"
      onClick={run}
      disabled={state === "working"}
      aria-label={label}
      title={label}
      className={`${ICON_BTN} ${state === "error" ? "border-red-400/60 text-red-400" : ""}`}
    >
      {state === "working" ? <span className="text-xs tabular-nums">{done}/{total}</span> : <DownloadIcon />}
    </button>
  );
}
